import { Provider, Type } from '@angular/core';
import {
	IAuthContext,
	AuthProvider,
	ProjectProvider,
	DataProvider,
	FilterProvider,
	NavbarProvider,
	IEvaluationService,
	IValidationService,
	IOptionService,
	IHelpService,
	IHelpExtensionHandler,
	IConfirmService,
	IGeodataApiService,
	IListDataProvider
} from '../interfaces';
import {
	AUTH_CONTEXT,
	AUTH_PROVIDER,
	PROJECT_PROVIDER,
	DATA_PROVIDER,
	FILTER_PROVIDER,
	NAVBAR_PROVIDER,
	FILTER_COMPONENT,
	SYNC_PROGRESS_COMPONENT,
	DOWNLOAD_PROGRESS_COMPONENT,
	OFFLINE_DETAIL_COMPONENT,
	KEYCLOAK_ADMIN_SERVICE,
	EVALUATION_SERVICE,
	VALIDATION_SERVICE,
	OPTION_SERVICE,
	HELP_SERVICE,
	HELP_EXTENSION_HANDLER,
	CONFIRM_SERVICE,
	GEODATA_API_SERVICE,
	FILTER_VALUES,
	SENSITIVE_FIELDS,
	PROJECT_ROLES_CONFIG,
	ProjectRolesConfig,
	APP_VERSION,
	CLEANUP_VERSIONS,
	VERSION_STORAGE_KEY,
	CURRENT_DATA_VERSION,
	BUILT_IN_MIGRATIONS,
	ROUTE_CACHE_TARGETS,
	WMS_CONFIG,
	DB_CONNECTION_CONFIGS,
	DOCUMENT_HANDLER,
	STRUCTURE_PROVIDERS,
	STRUCTURE_ASSIGNMENT_CONFIGS,
	StructureAssignmentConfig,
	OFFLINE_DOCUMENT_GROUPS,
	OfflineDocumentGroup,
	PROJECT_EXTRA_ACTIONS,
	ProjectExtraAction,
	PROJECT_NEW_SECTIONS,
	ProjectNewSection,
	PROJECT_POST_CREATE_HANDLERS,
	ProjectPostCreateHandler,
	PROJECT_DELETION_HANDLER,
	ProjectDeletionHandler,
	PROJECT_ENTITY_LABEL,
	ProjectEntityLabel,
	DGM_API_URL
} from '../tokens';
import { LIST_DATA_PROVIDER } from '../interfaces/list-data-provider.interface';
import { DocumentHandler } from '../data/document-handler.interface';
import { DbConnectionConfig } from '../db/db-connection-provider.interface';
import { MigrationDefinition } from '../types/migration.types';
import {
	MAP_MARKER_HANDLER,
	MAP_POLYGON_HANDLER,
	MAP_NAVIGATION_HANDLER,
	WMS_FIELD_MAPPING_HANDLER,
	GPKG_AUTO_STYLE_HANDLER,
	PROJECT_ASSIGNMENT_HANDLER,
	DATA_LIFECYCLE_HANDLER,
	POINT_TO_GEOJSON_HANDLER
} from '../../map/handlers';

/**
 * Konfiguration für provideLuzulaApp().
 * Pflichtfelder haben keinen Default — optionale nutzen die Factory-Defaults aus tokens.ts.
 */
export interface LuzulaAppConfig {
	/** Authentifizierung (Pflicht) */
	auth: {
		/** AuthProvider-Implementierung (z.B. AuthService) */
		provider: Type<AuthProvider>;
		/** IAuthContext-Implementierung — default: gleich wie provider */
		context?: Type<IAuthContext>;
	};

	/** Daten-Layer (Pflicht) */
	data: {
		/** DataProvider-Implementierung (z.B. DataService) */
		provider: Type<DataProvider<any>>;
		/** DocumentHandler-Implementierung */
		documentHandler: Type<DocumentHandler<any>>;
		/** Datenbank-Verbindungskonfiguration */
		dbConfigs: DbConnectionConfig[];
	};

	/** Projekt-Verwaltung (Pflicht) */
	project: {
		/** ProjectProvider-Implementierung (z.B. ProjectService) */
		provider: Type<ProjectProvider>;
	};

	/** Structure Services (optional — für hierarchische Domain-Daten wie Forstbetriebe, Abteilungen) */
	structure?: {
		/** Structure-Service-Implementierungen (z.B. ForstbetriebeService, AbteilungenService) */
		providers: Type<any>[];
		/** Konfiguration für Zuweisungs-Abschnitte im Projekt-Dialog */
		assignmentSections?: StructureAssignmentConfig[];
	};

	/** Filter-Feature (optional — NullFilterProvider als Default) */
	filter?: {
		provider?: Type<FilterProvider>;
		component?: Type<any>;
		values?: any;
	};

	/** Navbar-Provider (optional — SimpleNavbarProvider als Default) */
	navbar?: Type<NavbarProvider>;

	/** Service-Overrides (optional — Null-Implementierungen als Default) */
	services?: {
		evaluation?: Type<IEvaluationService>;
		validation?: Type<IValidationService>;
		option?: Type<IOptionService>;
		help?: Type<IHelpService>;
		helpExtension?: Type<IHelpExtensionHandler>;
		confirm?: Type<IConfirmService>;
		geodataApi?: Type<IGeodataApiService>;
		keycloakAdmin?: Type<any>;
		listData?: Type<IListDataProvider>;
	};

	/** Component-Referenzen (optional — Injections sind @Optional) */
	components?: {
		syncProgress?: Type<any>;
		downloadProgress?: Type<any>;
		offlineDetail?: Type<any>;
	};

	/** Map-Handler (optional — alle bereits @Optional in der Library) */
	handlers?: {
		mapMarker?: Type<any>;
		mapPolygon?: Type<any>;
		mapNavigation?: Type<any>;
		wmsFieldMapping?: Type<any>;
		gpkgAutoStyle?: Type<any>;
		projectAssignment?: Type<any>;
		dataLifecycle?: Type<any>;
		pointToGeojson?: Type<any>;
	};

	/** Extra-Aktionen im Projektmenü (optional) */
	projectActions?: ProjectExtraAction[];

	/** Zusätzliche Sektionen im "Neues Projekt"-Dialog (optional) */
	projectNewSections?: ProjectNewSection[];

	/** Post-Create-Handler nach Projekt-Erstellung (optional) */
	projectPostCreateHandlers?: ProjectPostCreateHandler[];

	/** Deletion-Handler für app-spezifische Löschlogik mit Progress (optional) */
	projectDeletionHandler?: Type<ProjectDeletionHandler>;

	/** Config-Werte (optional — alle haben Factory-Defaults) */
	config?: {
		sensitiveFields?: string[];
		projectRoles?: ProjectRolesConfig;
		wms?: any[];
		appVersion?: string;
		cleanupVersions?: string[];
		versionStorageKey?: string;
		currentDataVersion?: number;
		builtInMigrations?: MigrationDefinition[];
		routeCacheTargets?: string[];
		offlineDocumentGroups?: OfflineDocumentGroup[];
		projectEntityLabel?: ProjectEntityLabel;
		dgmApiUrl?: string;
	};
}

/**
 * Erzeugt alle Provider für eine luzula-lib-basierte App.
 *
 * Pflichtfelder (auth, data, project) müssen angegeben werden.
 * Optionale Felder werden nur gesetzt wenn angegeben — ansonsten greifen die Factory-Defaults.
 *
 * @example
 * ```typescript
 * providers: [
 *   ...provideLuzulaApp({
 *     auth: { provider: AuthService },
 *     data: { provider: DataService, documentHandler: MyDocHandler, dbConfigs: [...] },
 *     project: { provider: ProjectService },
 *   })
 * ]
 * ```
 */
export function provideLuzulaApp(config: LuzulaAppConfig): Provider[] {
	const providers: Provider[] = [];

	// Pflicht — Core Services
	providers.push(
		{ provide: AUTH_PROVIDER, useExisting: config.auth.provider },
		{ provide: AUTH_CONTEXT, useExisting: config.auth.context ?? config.auth.provider },
		{ provide: DATA_PROVIDER, useExisting: config.data.provider },
		{ provide: DOCUMENT_HANDLER, useClass: config.data.documentHandler },
		{ provide: DB_CONNECTION_CONFIGS, useValue: config.data.dbConfigs },
		{ provide: PROJECT_PROVIDER, useExisting: config.project.provider },
	);

	// Structure Services (multi-provider)
	if (config.structure?.providers) {
		for (const providerType of config.structure.providers) {
			providers.push({ provide: STRUCTURE_PROVIDERS, useExisting: providerType, multi: true });
		}
	}
	if (config.structure?.assignmentSections) {
		providers.push({ provide: STRUCTURE_ASSIGNMENT_CONFIGS, useValue: config.structure.assignmentSections });
	}

	// Filter
	if (config.filter?.provider)  providers.push({ provide: FILTER_PROVIDER, useExisting: config.filter.provider });
	if (config.filter?.component) providers.push({ provide: FILTER_COMPONENT, useValue: config.filter.component });
	if (config.filter?.values)    providers.push({ provide: FILTER_VALUES, useValue: config.filter.values });

	// Navbar
	if (config.navbar) providers.push({ provide: NAVBAR_PROVIDER, useExisting: config.navbar });

	// Services
	const s = config.services;
	if (s?.evaluation)    providers.push({ provide: EVALUATION_SERVICE, useExisting: s.evaluation });
	if (s?.validation)    providers.push({ provide: VALIDATION_SERVICE, useExisting: s.validation });
	if (s?.option)        providers.push({ provide: OPTION_SERVICE, useExisting: s.option });
	if (s?.help)          providers.push({ provide: HELP_SERVICE, useExisting: s.help });
	if (s?.helpExtension) providers.push({ provide: HELP_EXTENSION_HANDLER, useClass: s.helpExtension });
	if (s?.confirm)       providers.push({ provide: CONFIRM_SERVICE, useExisting: s.confirm });
	if (s?.geodataApi)    providers.push({ provide: GEODATA_API_SERVICE, useExisting: s.geodataApi });
	if (s?.keycloakAdmin) providers.push({ provide: KEYCLOAK_ADMIN_SERVICE, useExisting: s.keycloakAdmin });
	if (s?.listData)      providers.push({ provide: LIST_DATA_PROVIDER, useExisting: s.listData });

	// Components
	const c = config.components;
	if (c?.syncProgress)     providers.push({ provide: SYNC_PROGRESS_COMPONENT, useValue: c.syncProgress });
	if (c?.downloadProgress) providers.push({ provide: DOWNLOAD_PROGRESS_COMPONENT, useValue: c.downloadProgress });
	if (c?.offlineDetail)    providers.push({ provide: OFFLINE_DETAIL_COMPONENT, useValue: c.offlineDetail });

	// Map-Handler
	const h = config.handlers;
	if (h?.mapMarker)         providers.push({ provide: MAP_MARKER_HANDLER, useClass: h.mapMarker });
	if (h?.mapPolygon)        providers.push({ provide: MAP_POLYGON_HANDLER, useClass: h.mapPolygon });
	if (h?.mapNavigation)     providers.push({ provide: MAP_NAVIGATION_HANDLER, useClass: h.mapNavigation });
	if (h?.wmsFieldMapping)   providers.push({ provide: WMS_FIELD_MAPPING_HANDLER, useClass: h.wmsFieldMapping });
	if (h?.gpkgAutoStyle)     providers.push({ provide: GPKG_AUTO_STYLE_HANDLER, useClass: h.gpkgAutoStyle });
	if (h?.projectAssignment) providers.push({ provide: PROJECT_ASSIGNMENT_HANDLER, useClass: h.projectAssignment });
	if (h?.dataLifecycle)     providers.push({ provide: DATA_LIFECYCLE_HANDLER, useClass: h.dataLifecycle });
	if (h?.pointToGeojson)    providers.push({ provide: POINT_TO_GEOJSON_HANDLER, useClass: h.pointToGeojson });

	// Project Extra Actions
	if (config.projectActions) providers.push({ provide: PROJECT_EXTRA_ACTIONS, useValue: config.projectActions });

	// Project New Dialog Sections
	if (config.projectNewSections) providers.push({ provide: PROJECT_NEW_SECTIONS, useValue: config.projectNewSections });

	// Project Post-Create Handlers
	if (config.projectPostCreateHandlers) providers.push({ provide: PROJECT_POST_CREATE_HANDLERS, useValue: config.projectPostCreateHandlers });

	// Project Deletion Handler
	if (config.projectDeletionHandler) providers.push({ provide: PROJECT_DELETION_HANDLER, useExisting: config.projectDeletionHandler });

	// Config-Werte
	const cfg = config.config;
	if (cfg?.sensitiveFields)          providers.push({ provide: SENSITIVE_FIELDS, useValue: cfg.sensitiveFields });
	if (cfg?.projectRoles)             providers.push({ provide: PROJECT_ROLES_CONFIG, useValue: cfg.projectRoles });
	if (cfg?.wms)                      providers.push({ provide: WMS_CONFIG, useValue: cfg.wms });
	if (cfg?.appVersion)               providers.push({ provide: APP_VERSION, useValue: cfg.appVersion });
	if (cfg?.cleanupVersions)          providers.push({ provide: CLEANUP_VERSIONS, useValue: cfg.cleanupVersions });
	if (cfg?.versionStorageKey)        providers.push({ provide: VERSION_STORAGE_KEY, useValue: cfg.versionStorageKey });
	if (cfg?.currentDataVersion != null) providers.push({ provide: CURRENT_DATA_VERSION, useValue: cfg.currentDataVersion });
	if (cfg?.builtInMigrations)        providers.push({ provide: BUILT_IN_MIGRATIONS, useValue: cfg.builtInMigrations });
	if (cfg?.routeCacheTargets)        providers.push({ provide: ROUTE_CACHE_TARGETS, useValue: cfg.routeCacheTargets });
	if (cfg?.offlineDocumentGroups)    providers.push({ provide: OFFLINE_DOCUMENT_GROUPS, useValue: cfg.offlineDocumentGroups });
	if (cfg?.projectEntityLabel)       providers.push({ provide: PROJECT_ENTITY_LABEL, useValue: cfg.projectEntityLabel });
	if (cfg?.dgmApiUrl)                providers.push({ provide: DGM_API_URL, useValue: cfg.dgmApiUrl });

	return providers;
}
